import React from 'react';

import noop from 'lodash/noop';

import { margin, padding, separator, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { LoadMoreButton, LoadMoreButtonProps } from '@lumapps/lumx-buttons/components/LoadMoreButton';
import { Sorters } from '@lumapps/lumx-filter-and-sort/components/Sorters';
import { SORT_ORDER } from '@lumapps/lumx-filter-and-sort/constants';
import { ListState, ListStateProps } from '@lumapps/lumx-lists/components/ListState';
import { EditableListProps } from '@lumapps/lumx-lists/types/EditableList';
import {
    Alignment,
    ButtonSize,
    Checkbox,
    FlexBox,
    List,
    ListItem,
    Orientation,
    Size,
    SkeletonRectangle,
    ThOrder,
    Tooltip,
} from '@lumapps/lumx/react';
import { InfiniteScroll } from '@lumapps/lumx/react/utils';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { GenericEntityBlock, GenericEntityBlockProps } from '../GenericEntityBlock';
import { GenericEntityBlockSkeleton, GenericEntityBlockSkeletonProps } from '../GenericEntityBlockSkeleton';

import './index.scss';

export interface GenericEntityBlockListProps<T> extends EditableListProps<T>, ListStateProps<T> {
    /** props to be passed in to the skeleton component */
    skeletonProps?: GenericEntityBlockSkeletonProps;
    /**
     * function in order to retrieve the props to pass in to the `GenericEntityBlock` component.
     * If you already have your own created Block, use the `Block` prop instead.
     */
    getEntityBlockProps?: (entity: T) => GenericEntityBlockProps;
    /** to be displayed after the Sorters component */
    afterSorts?: React.ReactNode;
    /** to be displayed before block list */
    before?: React.ReactNode;
    /** component's scope for tracking purposes */
    scope: string;
    /**
     * Component in order to render the entity. Please use `GenericEntityBlock` under the hood.
     * If you do not have your own created Block, use the `getEntityBlockProps` prop instead.
     */
    Block?: (entity: T) => React.ReactNode;
    /**
     * Component in order to render the skeleton. Please use `GenericEntityBlockSkeleton` under the hood.
     * If you do not have your own created Skeleton, use the `skeletonProps` prop instead.
     */
    Skeleton?: React.ReactNode;
    /** element to be displayed after the select all checkbox */
    afterSelectAllCheckbox?: React.ReactNode;
    /**
     * custom class name
     */
    className?: string;
    /** display separator after items or no */
    hasSeparator?: boolean;
    /** using load more button instead infinity loading */
    hasLoadMoreButton?: boolean;
    /** load more button props */
    loadMoreButtonProps?: Partial<LoadMoreButtonProps>;
    /** default to true - whether to display or not the empty state */
    displayEmptyState?: boolean;
}

const CLASSNAME = 'lumx-generic-entity-block-list';
/**
 * Component that displays a list of items using the `GenericEntityBlock` pattern.
 *
 * @family Blocks
 * @param GenericEntityBlockListProps
 * @returns GenericEntityBlockList
 */
export const GenericEntityBlockList = <T,>({
    entities = [],
    hasError = false,
    hasMore = false,
    onLoadMore,
    isLoading = false,
    isOnlyOneRowSelectable = false,
    isSelectionDisabled = false,
    areRowsSelectable,
    areAllRowsSelectable,
    getEntityId,
    allSelected,
    setSelected = noop,
    setAllSelected,
    someSelected,
    selected = {},
    getCheckboxProps,
    oneSelected,
    onlySelected,
    skeletonProps,
    getEntityBlockProps,
    selectAllCheckboxProps,
    after,
    onSortChange,
    sort,
    sortOrder,
    sortersProps,
    afterSorts,
    scope,
    stickyToolbar,
    Block,
    Skeleton,
    className,
    setSelection,
    before,
    afterSelectAllCheckbox,
    hasSeparator = true,
    hasLoadMoreButton = false,
    loadMoreButtonProps,
    emptyStateProps,
    filteredStateProps,
    serviceNotAvailableProps,
    isFiltered,
    searchValue,
    iconSize = Size.xl,
    displayEmptyState = true,
}: GenericEntityBlockListProps<T>) => {
    const { element, block } = useClassnames(CLASSNAME);
    const { get } = useDataAttributes(scope);
    const translations = useTranslate();
    const { translateKey } = translations;
    const canAllRowsBeSelected = areAllRowsSelectable && !isOnlyOneRowSelectable;
    const hasAtLeastOneRowSelected = someSelected || oneSelected;
    const isIntermediateCheckboxState = hasAtLeastOneRowSelected && 'intermediate';
    const selectAllTooltipLabel = selectAllCheckboxProps?.tooltipLabel || translateKey(GLOBAL.SELECT_ALL_DESELECT_ALL);

    const onAllRowCheckboxClick = (isChecked: boolean) => {
        if (setAllSelected) {
            // Select all items on click on intermediate state checkbox
            if (hasAtLeastOneRowSelected && !allSelected) {
                setAllSelected(true);
            } else {
                setAllSelected(isChecked);
            }
        }
    };

    return (
        <>
            {before}
            {areAllRowsSelectable || onSortChange ? (
                <div className={element('actions-bar-wrapper', { sticky: Boolean(stickyToolbar) })}>
                    <FlexBox
                        orientation={Orientation.horizontal}
                        vAlign={
                            !canAllRowsBeSelected && !afterSelectAllCheckbox && onSortChange
                                ? Alignment.right
                                : Alignment.spaceBetween
                        }
                        className={element('actions-bar', { selected: Boolean(hasAtLeastOneRowSelected) }, [
                            hasSeparator && separator(),
                            padding('vertical', 'big'),
                            padding('horizontal', 'huge'),
                        ])}
                    >
                        {isLoading && canAllRowsBeSelected ? (
                            <SkeletonRectangle
                                className={margin('vertical', 'tiny')}
                                width={Size.xs}
                                height={Size.xs}
                            />
                        ) : null}

                        {!isLoading && canAllRowsBeSelected ? (
                            <Tooltip label={selectAllTooltipLabel}>
                                <Checkbox
                                    isChecked={allSelected || isIntermediateCheckboxState}
                                    onChange={onAllRowCheckboxClick}
                                    isDisabled={(entities && entities.length === 0) || hasError || isSelectionDisabled}
                                    aria-label={selectAllTooltipLabel}
                                    className={margin('vertical', 'tiny')}
                                    {...selectAllCheckboxProps}
                                />
                            </Tooltip>
                        ) : null}

                        {!isLoading ? afterSelectAllCheckbox : null}

                        {onSortChange ? (
                            <Sorters
                                shouldDisplaySortedByLabel
                                {...sortersProps}
                                sort={sort}
                                order={sortOrder === ThOrder.asc ? SORT_ORDER.INCREASING : SORT_ORDER.DECREASING}
                                onChange={(s, order) => {
                                    if (s && order) {
                                        onSortChange(s, order === SORT_ORDER.DECREASING ? ThOrder.desc : ThOrder.asc);
                                    }
                                }}
                                sortButtonProps={{
                                    size:
                                        sortersProps?.choices?.length === 1
                                            ? (Size.xxs as ButtonSize)
                                            : (Size.s as ButtonSize),
                                }}
                            />
                        ) : null}

                        {afterSorts}
                    </FlexBox>
                </div>
            ) : null}

            <ListState
                entities={entities}
                emptyStateProps={emptyStateProps}
                filteredStateProps={filteredStateProps}
                hasError={hasError}
                isLoading={isLoading}
                isFiltered={isFiltered}
                searchValue={searchValue}
                serviceNotAvailableProps={serviceNotAvailableProps}
                iconSize={iconSize}
                displayEmptyState={displayEmptyState}
            />

            <List itemPadding={Size.huge} className={block([className])}>
                {entities && entities.length > 0 && !hasError
                    ? entities.map((entity, index) => {
                          const entityId = getEntityId(entity);
                          //   allSelected can equal 'intermediate"
                          const isChecked = selected[entityId] || allSelected === true;
                          const onEntityCheck = (isChecked: boolean) => {
                              setSelected(entityId, isChecked);
                          };
                          const isDisabled = isOnlyOneRowSelectable && oneSelected && onlySelected !== entityId;
                          const checkboxProps = getCheckboxProps ? getCheckboxProps(entity, translations) : {};
                          const titleId = `${scope}-title-${entityId}`;
                          const checkboxLabel =
                              checkboxProps.label ||
                              checkboxProps['aria-label'] ||
                              translateKey(GLOBAL.SELECT_DESELECT);

                          const entityProps = getEntityBlockProps ? getEntityBlockProps(entity) : undefined;

                          return (
                              <React.Fragment key={entityId}>
                                  {/**
                                   * Add the infinity scroll observer above the last item to make sure it's triggered properly
                                   */}
                                  {index === entities.length - 1 && hasMore && onLoadMore && !hasLoadMoreButton ? (
                                      <InfiniteScroll
                                          callback={() => {
                                              if (!isLoading) {
                                                  onLoadMore();
                                                  // Only select the content previously selected on load more
                                                  if (setAllSelected && setSelection && allSelected) {
                                                      setAllSelected(false);
                                                      setSelection(selected);
                                                  }
                                              }
                                          }}
                                      />
                                  ) : null}
                                  <ListItem
                                      isSelected={isChecked}
                                      className={element(
                                          'item',
                                          { selected: Boolean(isChecked), disabled: Boolean(isDisabled) },
                                          [(index !== entities.length - 1 || isLoading) && hasSeparator && separator()],
                                      )}
                                      before={
                                          areRowsSelectable ? (
                                              <Tooltip label={checkboxLabel}>
                                                  <Checkbox
                                                      aria-label={checkboxLabel}
                                                      {...checkboxProps}
                                                      onChange={!isDisabled ? onEntityCheck : undefined}
                                                      isChecked={isChecked}
                                                      isDisabled={isLoading || isDisabled || isSelectionDisabled}
                                                      inputProps={{
                                                          ...checkboxProps.inputProps,
                                                          'aria-checked': isChecked,
                                                          role: 'checkbox',
                                                          'aria-describedby': titleId,
                                                      }}
                                                  />
                                              </Tooltip>
                                          ) : null
                                      }
                                  >
                                      {entityProps && !Block ? (
                                          <GenericEntityBlock
                                              {...entityProps}
                                              scope={scope}
                                              titleProps={{
                                                  ...entityProps.titleProps,
                                                  id: titleId,
                                              }}
                                          />
                                      ) : null}

                                      {!entityProps && Block ? Block(entity) : null}
                                  </ListItem>
                              </React.Fragment>
                          );
                      })
                    : null}

                {onLoadMore && hasMore && hasLoadMoreButton && !isLoading && (
                    <LoadMoreButton
                        onClick={() => onLoadMore()}
                        size={Size.s}
                        className={element('loading-more')}
                        {...get({ element: 'button', action: 'load-more' })}
                        {...loadMoreButtonProps}
                    />
                )}

                {isLoading ? (
                    <>
                        <ListItem
                            className={element('item', [hasSeparator && separator()])}
                            before={areRowsSelectable ? <SkeletonRectangle width={Size.xs} height={Size.xs} /> : null}
                        >
                            {Skeleton || <GenericEntityBlockSkeleton {...skeletonProps} />}
                        </ListItem>
                        <ListItem
                            className={element('item', [hasSeparator && separator()])}
                            before={areRowsSelectable ? <SkeletonRectangle width={Size.xs} height={Size.xs} /> : null}
                        >
                            {Skeleton || <GenericEntityBlockSkeleton {...skeletonProps} />}
                        </ListItem>
                        <ListItem
                            className={element('item')}
                            before={areRowsSelectable ? <SkeletonRectangle width={Size.xs} height={Size.xs} /> : null}
                        >
                            {Skeleton || <GenericEntityBlockSkeleton {...skeletonProps} />}
                        </ListItem>
                    </>
                ) : null}
            </List>

            {after}
        </>
    );
};
